/**
 * Card field → OrderInfo field. Card values are the same vocabulary as the form (benefit type included).
 *
 * Only the seven fields the submission form actually shows an input for. `memberName` and `state` are
 * deliberately absent: nothing on the form writes patientInsuranceMemberName or patientInsuranceState,
 * so a card carrying either would differ from a value the form can never hold — the picker would offer
 * that card forever and "Use this instead" would write values nobody can see, which is LAM-5511's dead
 * button all over again. The backend's own reconcile already takes that position: a blank submitted
 * field is "nothing to offer", never a discrepancy (patient_roster_sync._reconcile_field), which is why
 * it reports no insurance discrepancies for exactly the cards this picker kept offering.
 *
 * The cost is that a submission started from a card no longer inherits its insurance state, so the CMM
 * form and the fax PDF fall back to patient_state and the policy review runs stateless — which is what
 * every manually-filled submission did before this picker existed. Re-add `state` here only alongside a
 * real input for it.
 */
export const INSURANCE_CARD_TO_ORDER_FIELD = {
    benefitType: "benefitType",
    memberId: "patientMemberId",
    insuranceProvider: "insuranceProvider",
    insurancePlanType: "insurancePlanType",
    groupNumber: "grpNumber",
    rxBin: "bin",
    rxPcn: "processorControlNumber",
};
/** The non-empty (orderField, value) pairs a card contributes — blanks never overwrite what the form has. */
export function insuranceCardOrderFields(card) {
    return Object.entries(INSURANCE_CARD_TO_ORDER_FIELD).flatMap(([cardField, orderField]) => {
        const value = card[cardField];
        return value == null || value === "" ? [] : [[orderField, String(value)]];
    });
}
/**
 * The subset of insuranceCardOrderFields the order does not already carry — what adopting this card
 * would actually change.
 *
 * Compared trimmed on both sides because the two spellings reach here by different routes: the card
 * was stored through _display (trimmed server-side), the form value is whatever the user typed. A
 * card is applied raw, so only the comparison is trimmed, never the written value.
 */
export function insuranceCardChanges(card, order) {
    return insuranceCardOrderFields(card).filter(([orderField, value]) => String(order[orderField] ?? "").trim() !== value.trim());
}
